import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { generateRandomColor } from '../../utils/colorUtils';
import { HOSTNAME } from "../../configs/Domain";
import {sampleModernColors} from "../../utils/colors";
import {COOL_COLOR_PALETTE} from "../../utils/colorUtils";


const DEFAULT_START = '2019-01-03';

const comparisonInitialState = {
    items: [],
    commonStartDate: DEFAULT_START,
    commonEndDate: null,
    commonBenchmark: null,
    needRefetch: false, // Flag to determine if a refetch is needed
    loading: false,
    error: null,
    itemColors: {},
    badItems: [],
    status: 'idle',
    refireAttempts: 0,
}


/**
 *
 *
 * */
const fetchTimeSeriesData = createAsyncThunk(
  'comparison/fetchTimeSeriesData',
    /**
     *
     * @param symbols {Array[string]} Array of symbols to fetch
     * @param startDate {string, optional} Start date in format 'YYYY-MM-DD'
     * @param endDate {string, optional} End date in format 'YYYY-MM-DD'
     * @param benchmark {string, optional='spy} Benchmark symbol to compare against
     * @param labels {Array[object], optional} Array of objects with id and label properties [{'id': <idWithinSymbols>, 'label': <label>}]
     * @param getState
     * @param rejectWithValue
     */
  async ({ symbols, startDate = DEFAULT_START, endDate, benchmark, labels }, { getState, rejectWithValue }) => {
    const state = getState().comparison;
    console.log("PRECHECK")
    if (state.loading && state.refireAttempts > 1) return rejectWithValue('Fetch in progress');
    console.log("POSTCHECK")
    let url = `${HOSTNAME}/performance/compare?symbols=${symbols.join(',')}`;
    url += `&sd=${startDate}`;
    if (endDate) url += `&ed=${endDate}`;
    if (benchmark) url += `&benchmark=${benchmark}`;

    try {
      const response = await axios.get(url);
      const data = response.data;

      return { symbols, startDate, endDate, benchmark, data, labels };
    } catch (error) {
        console.log("ERROR ", error);
      return rejectWithValue(error.toString());
    }
  }
);




// Thunk to re-fetch all items with new common start date
export const refetchAllItems = createAsyncThunk(
  'comparison/refetchAllItems',
  async (_, { getState, dispatch }) => {
    const state = getState().comparison;
    const { items, commonStartDate } = state;
    for (const item of items) {
      await dispatch(fetchTimeSeriesData({ ...item, startDate: commonStartDate }));
    }
  }
);


const comparisonSlice = createSlice({
  name: 'comparison',
  initialState: comparisonInitialState,
  reducers: {
    removeItem: (state, action) => {
      const id = action.payload.id;
      state.items = state.items.filter(item => item.id !== id);
      delete state.itemColors[id];
      state.badItems = state.badItems.filter(item => item.id !== id);
    },
    resetComparison: (state) => {
      state.items = [];
      state.commonStartDate = DEFAULT_START;
      state.commonEndDate = null;
      state.needRefetch = false;
      state.loading = false;
      state.itemColors = {};
    },
    setCommonBenchmark: (state, action) => {
      state.commonBenchmark = action.payload;
    },
    updateItemColor: (state, action) => {
        const { id, color } = action.payload;
        if (state.itemColors[id]) {
            state.itemColors[id] = color;
        }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTimeSeriesData.pending, (state) => {
        if (state.status !== 'pending') {
          state.status = 'pending';
          state.loading = true;
          state.refireAttempts += 1;
        }
      })
      .addCase(fetchTimeSeriesData.fulfilled, (state, action) => {
        const { symbols, startDate, data, labels } = action.payload;
        state.loading = false;
        state.status = 'idle';
        state.refireAttempts = 0;
        // Update or add new items
        symbols.forEach(symbol => {
          const index = state.items.findIndex(item => item.id === symbol);
          const itemData = data.find(item => item.id === symbol);
          let itemLabel = symbol.toUpperCase();
          if (index === -1) {
              const currentItems = state.items.length;

            const newColor = currentItems < COOL_COLOR_PALETTE.length ? COOL_COLOR_PALETTE[currentItems] : sampleModernColors(1)[0];
            if (labels){
                const label = labels.find(label=>label.id === symbol);
                if (label) itemLabel = label.label;
            }


            state.items.push({ id: symbol, startDate, ...itemData.data, label: itemLabel, needsUpdate: false, color: newColor });
            state.itemColors[symbol] = newColor;
          } else {
            state.items[index] = { ...state.items[index], startDate, ...itemData.data, needsUpdate: false, label: itemLabel };
          }
        });
      })
      .addCase(fetchTimeSeriesData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  }
});

export const { reducer: comparisonReducer } = comparisonSlice;
export const comparisonActions = { ...comparisonSlice.actions, fetchTimeSeriesData, refetchAllItems };
